import { useEffect, useState } from "react";
import { Eye, Download, MoreVertical, Trash2 } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { SubTabBar } from "@/components/SubTabBar";
import { DocumentTypeIcon } from "@/components/DocumentTypeIcon";
import { groupByMainTab } from "@/application/navigation";
import { fetchCompanyProfile, saveCompanyProfile, type CompanyProfile } from "@/repositories/companyRepository";
import {
  fetchAdminDocuments,
  uploadDocument,
  deleteAdminDocument,
  DOCUMENT_CATEGORIES,
  type DocumentRow,
} from "@/repositories/adminDocumentsRepository";
import { formatDate } from "@/utils/format";

function driveViewUrl(driveFileId: string) {
  return `https://drive.google.com/file/d/${driveFileId}/view`;
}
function driveDownloadUrl(driveFileId: string) {
  return `https://drive.google.com/uc?export=download&id=${driveFileId}`;
}

// Shows the document's human title with its file extension appended, e.g.
// "Shareholder Agreement.pdf" — unless the stored Title already ends with
// that extension (true for documents uploaded before Title/extension were
// split apart, whose Title is still the raw original filename).
function documentDisplayName(doc: DocumentRow): string {
  const ext = doc.documentType.toLowerCase();
  if (!ext) return doc.title;
  return doc.title.toLowerCase().endsWith(`.${ext}`) ? doc.title : `${doc.title}.${ext}`;
}

const EMPTY: CompanyProfile = {
  companyId: "",
  tradingName: "",
  legalName: "",
  companyType: "",
  industry: "",
  incorporationCountry: "",
  jurisdiction: "",
  email: "",
  phone: "",
  website: "",
  status: "Active",
  currency: "",
  registeredAddress: "",
  operatingAddress: "",
  businessNumber: "",
  foundingDate: "",
  incorporationDate: "",
  fiscalYearEnd: "",
  legalStatus: "Active",
  registrationStatus: "",
  notes: "",
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="form-field">
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export function AdminCompanyLegalPage() {
  const [form, setForm] = useState<CompanyProfile>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const [documents, setDocuments] = useState<DocumentRow[] | null>(null);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState<string>("Company");
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");

  const siblingScreens = groupByMainTab("Admin").find((g) => g.mainTab === "Company")?.screens ?? [];

  function reloadDocuments() {
    fetchAdminDocuments()
      .then(setDocuments)
      .catch((e) => setDocsError(e instanceof Error ? e.message : String(e)));
  }

  useEffect(() => {
    fetchCompanyProfile()
      .then((data) => setForm(data))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));

    reloadDocuments();
  }, []);

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    const dot = file.name.lastIndexOf(".");
    setUploadTitle(dot > 0 ? file.name.slice(0, dot) : file.name);
    setUploadDescription("");
  }

  function cancelUpload() {
    setPendingFile(null);
    setUploadTitle("");
    setUploadDescription("");
  }

  async function handleSubmitUpload() {
    if (!pendingFile) return;
    setUploading(true);
    setDocsError(null);
    try {
      await uploadDocument(pendingFile, uploadCategory, uploadTitle, undefined, uploadDescription);
      reloadDocuments();
      cancelUpload();
    } catch (err) {
      setDocsError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(documentId: string) {
    if (!window.confirm("Delete this document? This removes the file from Drive too and can't be undone.")) {
      return;
    }
    setDeletingId(documentId);
    setOpenMenuId(null);
    try {
      await deleteAdminDocument(documentId);
      reloadDocuments();
    } catch (err) {
      setDocsError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletingId(null);
    }
  }

  function update<K extends keyof CompanyProfile>(key: K, value: CompanyProfile[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaveMessage(null);
    try {
      await saveCompanyProfile(form);
      setSaveMessage("Changes saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AppShell portal="Admin" userName="Admin" userRole="Admin">
      <div className="breadcrumb">Admin / Company / Legal & Registration</div>
      <SubTabBar screens={siblingScreens} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 26 }}>Company</h1>
        <button className="btn-primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {loading && <div className="loading-state">Loading registration details…</div>}
      {error && <p style={{ color: "#dc2626" }}>{error}</p>}
      {saveMessage && <p style={{ color: "#16a34a" }}>{saveMessage}</p>}

      {!loading && (
        <div className="card" style={{ maxWidth: 820 }}>
          <h2 className="card-title">Registration Details</h2>
          <div className="form-grid-3">
            <Field label="Country">
              <input
                className="form-input"
                value={form.incorporationCountry}
                onChange={(e) => update("incorporationCountry", e.target.value)}
                placeholder="e.g. Canada"
              />
            </Field>
            <Field label="Founding Date">
              <input
                className="form-input"
                type="date"
                value={form.foundingDate}
                onChange={(e) => update("foundingDate", e.target.value)}
              />
            </Field>
            <Field label="Legal Status">
              <select
                className="form-input"
                value={form.legalStatus}
                onChange={(e) => update("legalStatus", e.target.value)}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </Field>
            <Field label="Jurisdiction">
              <input
                className="form-input"
                value={form.jurisdiction}
                onChange={(e) => update("jurisdiction", e.target.value)}
                placeholder="e.g. Québec"
              />
            </Field>
            <Field label="Incorporation Date">
              <input
                className="form-input"
                type="date"
                value={form.incorporationDate}
                onChange={(e) => update("incorporationDate", e.target.value)}
              />
            </Field>
            <Field label="Reporting Currency">
              <input
                className="form-input"
                value={form.currency}
                onChange={(e) => update("currency", e.target.value)}
                placeholder="e.g. USD"
              />
            </Field>
            <Field label="Tax / Business Number">
              <input
                className="form-input"
                value={form.businessNumber}
                onChange={(e) => update("businessNumber", e.target.value)}
                placeholder="e.g. 1175879981"
              />
            </Field>
            <Field label="Registration Status">
              <input
                className="form-input"
                value={form.registrationStatus}
                onChange={(e) => update("registrationStatus", e.target.value)}
                placeholder="e.g. Verified"
              />
            </Field>
            <Field label="Fiscal Year End">
              <input
                className="form-input"
                value={form.fiscalYearEnd}
                onChange={(e) => update("fiscalYearEnd", e.target.value)}
                placeholder="e.g. 31 December"
              />
            </Field>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 className="card-title" style={{ margin: 0 }}>Registration Documents</h2>
          {!pendingFile && (
            <label className="btn-secondary" style={{ cursor: "pointer" }}>
              Upload Document
              <input type="file" onChange={handleFileSelect} style={{ display: "none" }} />
            </label>
          )}
        </div>

        {pendingFile && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              padding: 16,
              marginBottom: 16,
              border: "1px solid var(--border)",
              borderRadius: 8,
              background: "var(--surface-subtle, rgba(0,0,0,0.02))",
            }}
          >
            <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>File: {pendingFile.name}</p>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px" }}>
                <Field label="Title">
                  <input
                    className="form-input"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    placeholder="Document title"
                  />
                </Field>
              </div>
              <div style={{ flex: "1 1 180px" }}>
                <Field label="Category">
                  <select
                    className="form-input"
                    value={uploadCategory}
                    onChange={(e) => setUploadCategory(e.target.value)}
                  >
                    {DOCUMENT_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>
            <Field label="Description (optional)">
              <textarea
                className="form-input"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="What is this document, and why would an investor need it?"
                rows={2}
              />
            </Field>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-primary" onClick={handleSubmitUpload} disabled={uploading || !uploadTitle.trim()}>
                {uploading ? "Uploading…" : "Upload"}
              </button>
              <button className="btn-secondary" onClick={cancelUpload} disabled={uploading}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {docsError && <p style={{ color: "#dc2626" }}>{docsError}</p>}
        {!documents && !docsError && <div className="loading-state">Loading documents…</div>}

        {documents && documents.length === 0 && (
          <p style={{ color: "var(--text-muted)", fontSize: 14 }}>
            No documents yet. Documents uploaded to the Documents sheet will appear here.
          </p>
        )}

        {documents && documents.length > 0 && (
          <div className="doc-table-scroll">
          <table className="doc-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Category</th>
                <th>Visibility</th>
                <th>Updated</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.documentId}>
                  <td>
                    <span style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <DocumentTypeIcon type={doc.documentType} />
                      <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ fontSize: 13, color: doc.description ? "var(--text)" : "var(--text-muted)", fontStyle: doc.description ? "normal" : "italic" }}>
                          {doc.description || "No description"}
                        </span>
                        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          {documentDisplayName(doc)}
                        </span>
                      </span>
                    </span>
                  </td>
                  <td>{doc.category || "—"}</td>
                  <td>{doc.visibility}</td>
                  <td>{doc.uploadDate ? formatDate(doc.uploadDate) : "—"}</td>
                  <td>
                    <span className={`status-pill ${doc.status === "Active" ? "green" : "gray"}`}>
                      {doc.status === "Active" ? "Published" : doc.status || "—"}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, position: "relative" }}>
                      {doc.driveFileId ? (
                        <a href={driveViewUrl(doc.driveFileId)} target="_blank" rel="noreferrer" title="Preview">
                          <Eye size={16} style={{ color: "#2f5fdc" }} />
                        </a>
                      ) : (
                        <Eye size={16} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
                      )}

                      {doc.canDownload && doc.driveFileId ? (
                        <a href={driveDownloadUrl(doc.driveFileId)} target="_blank" rel="noreferrer" title="Download">
                          <Download size={16} style={{ color: "#2f5fdc" }} />
                        </a>
                      ) : (
                        <Download size={16} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
                      )}

                      <button
                        onClick={() => setOpenMenuId(openMenuId === doc.documentId ? null : doc.documentId)}
                        style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex" }}
                        title="More options"
                      >
                        <MoreVertical size={16} style={{ color: "#2f5fdc" }} />
                      </button>

                      {openMenuId === doc.documentId && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            right: 0,
                            zIndex: 10,
                            background: "var(--surface)",
                            border: "1px solid var(--border)",
                            borderRadius: 8,
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            minWidth: 140,
                            overflow: "hidden",
                          }}
                        >
                          <button
                            onClick={() => handleDelete(doc.documentId)}
                            disabled={deletingId === doc.documentId}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              width: "100%",
                              padding: "8px 12px",
                              background: "none",
                              border: "none",
                              color: "#dc2626",
                              cursor: "pointer",
                              fontSize: 13,
                              textAlign: "left",
                            }}
                          >
                            <Trash2 size={14} />
                            {deletingId === doc.documentId ? "Deleting…" : "Delete"}
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
